import { useEffect, useMemo, useRef } from "react";
import { CommunicationType } from "generated-graphql/graphql";
import * as EncampCommunication from "encamp-communication/client";
import { useCommunicationFormContext } from "./useCommunicationForm";

// Generate a TierII email subject using the template system and real form data
const refreshTemplate = ({
  tenant,
  facilities,
  tierIIReports,
  submissions,
}: {
  tenant: any;
  facilities: any[];
  tierIIReports: any[];
  submissions: any[];
}) => {
  // If we don't have the minimum required data, fall back to a simple format
  if (!tenant) {
    return {
      subject: "",
      bodyEnriched: {
        htmlTemplate: "",
        htmlHydrated: "",
      },
    };
  }

  // Create communication object using real form data
  const communication: EncampCommunication.TemplateCommunication = {
    tenant: tenant,
    facilities:
      facilities?.map((f) => ({
        ...f,
        streetAddress1: f.streetAddress1 ?? null,
        city: f.city ?? null,
        state: f.state ?? null,
        zip: f.zip ?? null,
      })) ?? [],
    tierIIReports: tierIIReports ?? [],
    submissions: submissions ?? [],
  };

  // Use the appropriate template
  const template = EncampCommunication.getTemplate(
    EncampCommunication.TemplateName.MailRoomFlexible
  );

  return {
    subject: template.subject({ communication }),
    bodyEnriched: {
      htmlTemplate: template.rawTemplate(),
      htmlHydrated: template.text({ communication }),
    },
  };
};

/**
 * Side effect hook that automatically syncs email subjects for all recipients
 * when communication type is Email and relevant form data changes.
 */
export const useAutoUpdateEmailSubjects = () => {
  const { watch, setValue } = useCommunicationFormContext();

  const recipients = watch("recipients");
  const tenant = watch("tenant");
  const facilities = watch("facilities");
  const tierIIReports = watch("tierIIReports");
  const submissions = watch("submissions");

  const communicationTypes =
    recipients?.map((r) => r.communicationType).join(",") || "";

  // Track previous recipients as a map of rowId to recipient
  // this allows us to only refresh if the communication type changes
  const prevRecipientsRef = useRef<{
    [rowId: string]: { communicationType: CommunicationType };
  }>({});

  useEffect(() => {
    if (!recipients) return;

    recipients.forEach((recipient, index) => {
      if (recipient.communicationType !== CommunicationType.Email) return;
      const prev = prevRecipientsRef.current[recipient.rowId];
      // Only refresh if new, or if communicationType or emailRecipientAddresses changed
      if (!prev || prev.communicationType !== recipient.communicationType) {
        const { subject, bodyEnriched } = refreshTemplate({
          tenant,
          facilities,
          tierIIReports,
          submissions,
        });
        setValue(`recipients.${index}.emailSubject`, subject, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
        setValue(`recipients.${index}.emailBodyEnriched`, bodyEnriched, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    });
    // Update the ref to the current recipients as a map
    prevRecipientsRef.current = Object.fromEntries(
      recipients.map((r: any) => [
        r.rowId,
        { communicationType: r.communicationType },
      ])
    );
  }, [
    recipients,
    tenant,
    facilities,
    tierIIReports,
    submissions,
    setValue,
    communicationTypes,
  ]);
};
